import Immutable from 'immutable';
import {asciifold} from 'utils';
import {queryMinLength} from './reducer';

export const sanitizeQuery = (query) => (
    asciifold(query)
        .replace(/\.+/g, '.')
        .replace(/("|¿|¡|,|!|\?|\.)/g, '')
        .replace(/-$/g, '')
        .replace(/\s+/g, ' ')
        .trim()
);

export const searchTranscript = (elementList, query) => {
    const queryWords = query.split(' ');
    const lastQueryWordIndex = queryWords.length - 1;
    const results = [];
    let queryWordIndex = 0;
    let currentTokens = [];

    elementList.get('tokens').forEach(token => {
        const value = token.get('value');
        const queryWord = queryWords[queryWordIndex];
        const matchIndex = value.indexOf(queryWord);
        const headMatches = queryWordIndex < lastQueryWordIndex && value === queryWord;
        const tailMatchesFromBeginning = queryWordIndex === lastQueryWordIndex && matchIndex === 0;
        const tailMatchesPartial = lastQueryWordIndex === 0 && matchIndex >= 0;

        if (headMatches || tailMatchesFromBeginning || tailMatchesPartial) {
            currentTokens.push(token.set('range', Immutable.List([matchIndex, queryWord.length])));
            queryWordIndex++;

            if (currentTokens.length === queryWords.length) {
                results.push({
                    displayAs: currentTokens.map(t => t.get('displayAs')).join(' '),
                    value: currentTokens.map(t => t.get('value')).join(' '),
                    startTime: currentTokens[0].get('startTime'),
                    endTime: currentTokens[currentTokens.length - 1].get('endTime'),
                    tokens: currentTokens,
                    type: 'Transcript',
                });
                queryWordIndex = 0;
                currentTokens = [];
            }
        } else if (queryWordIndex > 0) {
            queryWordIndex = 0;
            currentTokens = [];
        }
    });

    return Immutable.fromJS(results);
};

const matchPartially = (query) => (obj) => obj.get('value').includes(query);
const matchExactly = (query) => (obj) => obj.get('value') === query;
const getValue = (obj) => obj.get('value');
const getStartTimes = (objs) => objs.map(obj => obj.get('startTime'));
const uniqueStartTime = (startTimes) => (obj) => !startTimes.includes(obj.get('startTime'));

const calculateRange = (query) => (obj) => obj.merge({
    range: [obj.get('value').indexOf(query), query.length],
});

const toEntityGroup = (group) => Immutable.fromJS({
    count: group.reduce((r, v) => r + v.get('count'), 0),
    value: group.getIn([0, 'value']),
    displayAs: group.getIn([0, 'displayAs']),
    types: group.map(entity => entity.get('type')).toSet().toList(),
    timeRanges: group.flatMap(entity => entity.get('timeRanges')),
});

const toTokenGroup = (group) => Immutable.fromJS({
    count: group.size,
    value: group.getIn([0, 'value']),
    displayAs: group.getIn([0, 'displayAs']),
    types: ['Transcript'],
});

const getTokensForEntity = (elementList) => (entity) => {
    const query = sanitizeQuery(entity.get('value'));

    const tokens = searchTranscript(elementList, query)
        .filter(matchExactly(query))
        .filter(uniqueStartTime(getStartTimes(entity.get('timeRanges'))));

    if (tokens.size > 0 && !entity.get('types').includes('keyword')) {
        return entity
            .update('count', count => count + tokens.size)
            .update('types', types => types.push('Transcript'));
    }
    return entity;
};

export const expandTimeRanges = (entity) => (
    entity.get('timeRanges').map(timeRange => entity.delete('timeRanges').merge(timeRange))
);

export const getSuggestions = (elementList, query) => {
    query = sanitizeQuery(query);
    if (query.length < queryMinLength) return Immutable.List();

    const entities = elementList.get('entities')
        .filter(matchPartially(query))
        .groupBy(getValue)
        .map(toEntityGroup)
        .toList()
        .map(getTokensForEntity(elementList));

    const tokens = searchTranscript(elementList, query)
        .groupBy(getValue)
        .filter((_, tokenValue) => !entities.find(matchExactly(tokenValue)))
        .map(toTokenGroup);

    const suggestions = entities.concat(tokens)
        .map(calculateRange(query))
        .sortBy(suggestion => {
            if (matchExactly(query)(suggestion)) return -999999;
            return suggestion.get('count') * -1;
        });

    return suggestions;
};

export const getResults = (elementList, {query, types = Immutable.List(), partial = false}) => {
    query = sanitizeQuery(query);
    if (query.length < queryMinLength) return Immutable.List();

    const entities = elementList.get('entities')
        .filter(partial ? matchPartially(query) : matchExactly(query))
        .filter(entity => types.size === 0 || types.includes(entity.get('type')))
        .flatMap(expandTimeRanges);

    let tokens = Immutable.List();
    if (types.size === 0 || types.includes('Transcript')) {
        tokens = searchTranscript(elementList, query).filter(uniqueStartTime(getStartTimes(entities)));
        if (!partial) tokens = tokens.filter(matchExactly(query));
    }

    return entities.concat(tokens).sortBy(result => result.get('startTime'));
};



// WEBPACK FOOTER //
// ./scripts/store/search/helpers.js